import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Clock, Music, BookOpen, Gamepad2, Heart, Sparkles, Users, Star, Sprout, ChevronDown, Shield, ArrowRight, CheckCircle, Quote, Brain, UserCheck, MessageCircle, Handshake, Download, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const LOGO_RAIZES_DARK_URL = "/logos/logo_raizes_dark.png";
const LOGO_RAIZES_WHITE_URL = "/logos/logo_raizes_white.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SectionTitle = ({ icon: Icon, label, title, light }) => (
  <div className="text-center mb-12">
    <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-inter font-semibold mb-4 ${light ? 'bg-white/10 text-white/80' : 'bg-[#E8F5ED] text-[#1A4D2E]'}`}>
      <Icon className="w-4 h-4" /> {label}
    </span>
    <h2 className={`font-nunito text-3xl sm:text-4xl font-extrabold leading-tight ${light ? 'text-white' : 'text-[#1A4D2E]'}`}>{title}</h2>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => {
    axios.get(`${API}/landing-content`).then(res => setC(res.data.content)).catch(() => {});
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!c) {
    return (
      <div className="min-h-screen bg-[#FAFCFB] flex items-center justify-center">
        <Sprout className="w-10 h-10 text-[#1A4D2E] animate-pulse" />
      </div>
    );
  }

  const heroImg = c.hero_img || "/images/raizes_culto.jpg";
  const kidsImg = c.kids_img || "/images/raizes_criancas.jpg";
  const prayerImg = c.prayer_img || "/images/raizes_intercessao.jpg";

  return (
    <div className="min-h-screen bg-[#FAFCFB] overflow-x-hidden" style={{ fontFamily: "'Nunito', 'Inter', sans-serif" }}>

      {/* ===== NAVBAR ===== */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <img src={LOGO_RAIZES_DARK_URL} alt="Ministério Raízes" className="h-10 object-contain" />
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="flex items-center gap-1.5 text-xs sm:text-sm font-inter font-semibold text-[#1A4D2E] hover:text-[#145A32] transition-colors px-2 sm:px-3 py-2 rounded-lg hover:bg-[#E8F5ED]">
              <Shield className="w-4 h-4" /> <span className="hidden sm:inline">Área</span> Admin
            </button>
            <Button onClick={() => navigate('/app-pais/login')} className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-4 sm:px-5 h-9 text-xs sm:text-sm font-semibold shadow-md">
              Portal da Familia
            </Button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[92vh] flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Crianças adorando" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a2914]/90 via-[#1A4D2E]/80 to-[#1A4D2E]/60" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/10">
              <Sprout className="w-5 h-5 text-[#8BC34A]" />
              <span className="font-inter text-sm text-white/90 font-medium">{c.hero_faixa_etaria}</span>
            </div>
            <h1 className="font-nunito text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-6">
              Ministério<br />
              <span className="text-[#8BC34A]">{c.hero_titulo_destaque}</span>
            </h1>
            <p className="font-nunito text-xl sm:text-2xl text-white/90 font-semibold mb-1">
              {c.hero_subtitulo}
            </p>
            <p className="font-inter text-sm text-white/60 italic mb-4">
              {c.hero_versiculo_ref}
            </p>
            <p className="font-inter text-base text-white/70 leading-relaxed mb-8 max-w-lg">
              {c.hero_descricao}
            </p>
            <div className="flex flex-wrap gap-4 text-white/80 font-inter text-sm mb-10">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#8BC34A]" /> {c.hero_local}</span>
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#8BC34A]" /> {c.hero_dia}</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#8BC34A]" /> {c.hero_horario}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => scrollToSection('sobre')} variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-semibold backdrop-blur-sm">
                Quero Saber Mais <ChevronDown className="w-5 h-5 ml-1" />
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8">
              <img src="/logos/logo_igreja_familia.png" alt="Igreja Família" className="h-10 opacity-90" />
              <img src="/logos/logo_clubinho.png" alt="Clubinho" className="h-10 opacity-90" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/40" />
        </div>
      </section>

      {/* ===== SOBRE ===== */}
      <section id="sobre" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <SectionTitle icon={Sprout} label="SOBRE O MINISTÉRIO" title="O que é o Ministério Raízes?" />
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-inter text-gray-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: c.sobre_texto1 }} />
              <p className="font-inter text-gray-600 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: c.sobre_texto2 }} />
              <p className="font-inter text-gray-600 mb-5">Nossa missão é ajudar crianças a desenvolver:</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Sprout, text: 'Raízes profundas na fé', color: 'bg-green-50 text-green-700' },
                  { icon: Handshake, text: 'Senso de pertencimento', color: 'bg-blue-50 text-blue-700' },
                  { icon: Heart, text: 'Identidade em Cristo', color: 'bg-rose-50 text-rose-700' },
                  { icon: Sparkles, text: 'Amizades saudáveis', color: 'bg-amber-50 text-amber-700' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl ${item.color} border border-current/10`}>
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-inter text-sm font-semibold">{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-[#F2F7F4] rounded-2xl p-5 border border-[#E1ECE5]">
                <p className="font-inter text-sm text-gray-600"><strong className="text-[#1A4D2E]">Líderes:</strong> {c.sobre_lideres}</p>
                <p className="font-inter text-sm text-gray-600 mt-1"><strong className="text-[#1A4D2E]">Pastora responsável:</strong> {c.sobre_pastora}</p>
              </div>
            </div>
            <div className="relative">
              <img src={kidsImg} alt="Crianças reunidas" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#8BC34A]/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROPÓSITO ===== */}
      <section className="py-20 px-4 bg-[#1A4D2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }} />
        <div className="max-w-4xl mx-auto relative z-10">
          <SectionTitle icon={Heart} label="NOSSO PROPÓSITO" title="Por que o Ministério Raízes existe?" light />
          <div className="space-y-6 font-inter text-white/80 text-base leading-relaxed text-center max-w-2xl mx-auto">
            <p>{c.proposito_texto1}</p>
            <p>{c.proposito_texto2}</p>
            <p className="text-white font-semibold">{c.proposito_texto3}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 max-w-xl mx-auto">
            {['Seguro', 'Saudável', 'Presença de Deus', 'Pertencimento'].map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3 text-center">
                <CheckCircle className="w-5 h-5 text-[#8BC34A] mx-auto mb-1.5" />
                <span className="font-inter text-sm text-white font-semibold">{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-14 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center max-w-xl mx-auto">
            <Quote className="w-8 h-8 text-[#8BC34A] mx-auto mb-3 opacity-60" />
            <p className="font-nunito text-lg text-white italic leading-relaxed">
              "{c.proposito_versiculo}"
            </p>
            <p className="font-inter text-sm text-[#8BC34A] mt-3 font-semibold">{c.proposito_versiculo_ref}</p>
          </div>
        </div>
      </section>

      {/* ===== PRÉ-ADOLESCÊNCIA ===== */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <SectionTitle icon={Users} label="A IMPORTÂNCIA DA PRÉ-ADOLESCÊNCIA" title="Uma fase decisiva" />
          <p className="text-center font-inter text-gray-600 text-lg mb-4 max-w-2xl mx-auto">
            O pré-adolescente de hoje será o adolescente de amanhã.
          </p>
          <p className="text-center font-inter text-gray-500 mb-12 max-w-2xl mx-auto">
            Entre 8 e 11 anos acontecem mudanças importantes:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Brain, title: 'Desenvolvimento emocional', desc: 'Emoções se intensificam e precisam ser compreendidas', color: 'border-purple-200 bg-purple-50' },
              { icon: UserCheck, title: 'Formação da identidade', desc: 'Começam a definir quem são e no que acreditam', color: 'border-blue-200 bg-blue-50' },
              { icon: Handshake, title: 'Busca por pertencimento', desc: 'Querem fazer parte de algo maior que eles mesmos', color: 'border-green-200 bg-green-50' },
              { icon: MessageCircle, title: 'Influência das amizades', desc: 'Os amigos passam a ter papel central nas decisões', color: 'border-amber-200 bg-amber-50' },
            ].map((item, i) => (
              <div key={i} className={`rounded-2xl border ${item.color} p-6 text-center transition-all hover:shadow-lg hover:-translate-y-1`}>
                <item.icon className="w-10 h-10 mx-auto mb-3 text-[#1A4D2E]" />
                <h3 className="font-nunito font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="font-inter text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6 items-center max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={prayerImg} alt="Crianças em oração" className="w-full object-cover aspect-[4/3]" />
            </div>
            <div className="bg-[#F2F7F4] border border-[#E1ECE5] rounded-2xl p-6 text-center">
              <p className="font-inter text-gray-600 leading-relaxed">
                Quando encontram uma <strong className="text-[#1A4D2E]">comunidade saudável dentro da igreja</strong>, os pré-adolescentes desenvolvem raízes espirituais fortes que os acompanharão por toda a vida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ENCONTROS ===== */}
      <section className="py-20 px-4 bg-[#F2F7F4]">
        <div className="max-w-6xl mx-auto">
          <SectionTitle icon={Calendar} label="COMO SÃO NOSSOS ENCONTROS" title="Um culto preparado especialmente para pré-adolescentes" />
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: MapPin, label: 'Local', value: `${c.encontros_local_nome}\n${c.encontros_local_sala}\n${c.encontros_local_cidade}` },
                { icon: Calendar, label: 'Dia', value: c.encontros_dia },
                { icon: Clock, label: 'Horário', value: c.encontros_horario },
                { icon: Users, label: 'Idade', value: c.encontros_idade },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
                  <item.icon className="w-8 h-8 mx-auto mb-2 text-[#1A4D2E]" />
                  <p className="font-inter text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">{item.label}</p>
                  <p className="font-nunito font-bold text-[#1A4D2E] text-sm whitespace-pre-line">{item.value}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="font-nunito font-bold text-[#1A4D2E] text-lg mb-5">O que acontece nos encontros?</h3>
              <div className="space-y-3">
                {[
                  { icon: Music, text: 'Louvor', color: 'text-purple-600 bg-purple-50' },
                  { icon: BookOpen, text: 'Palavra bíblica', color: 'text-blue-600 bg-blue-50' },
                  { icon: Gamepad2, text: 'Dinâmicas e atividades', color: 'text-orange-600 bg-orange-50' },
                  { icon: Heart, text: 'Momentos de oração', color: 'text-rose-600 bg-rose-50' },
                  { icon: Sparkles, text: 'Comunhão e diversão', color: 'text-amber-600 bg-amber-50' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-4 px-5 py-3.5 rounded-xl ${item.color} border border-current/5 transition-all hover:shadow-sm`}>
                    <item.icon className="w-6 h-6 flex-shrink-0" />
                    <span className="font-inter font-semibold text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="font-inter text-sm text-gray-500 mt-5 italic">
                Tudo adaptado para a realidade e linguagem dos pré-adolescentes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BENEFÍCIOS ===== */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <SectionTitle icon={Star} label="BENEFÍCIOS DO MINISTÉRIO" title="O que seu filho ganha participando" />
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Desenvolver amizades saudáveis dentro da igreja',
              'Fortalecer sua identidade em Cristo',
              'Criar hábitos espirituais desde cedo',
              'Aprender princípios e valores bíblicos',
              'Se preparar emocionalmente para a adolescência',
              'Participar de discipulados chamados GF - Pequenos Grupos Familiares',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 transition-all hover:shadow-md hover:border-[#1A4D2E]/20">
                <CheckCircle className="w-5 h-5 text-[#8BC34A] flex-shrink-0 mt-0.5" />
                <span className="font-inter text-sm text-gray-700 font-medium">{text}</span>
              </div>
            ))}
          </div>
          <p className="text-center font-inter text-gray-500 mt-8">
            Nosso desejo é que cada criança se sinta <strong className="text-[#1A4D2E]">amada, acolhida e direcionada por Deus</strong>.
          </p>
        </div>
      </section>

      {/* ===== PROJETO SEMENTES ===== */}
      <section className="py-20 px-4 bg-[#1A4D2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <SectionTitle icon={Sprout} label="PROJETO SEMENTES" title="Crescimento espiritual de forma divertida" light />
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-inter text-white/80 leading-relaxed mb-6">
                Para aumentar o engajamento das crianças, criamos o <strong className="text-white">Projeto Sementes</strong>, que utiliza um sistema de gamificação.
              </p>
              <p className="font-inter text-white/70 mb-6">
                Os participantes acumulam pontos ao praticar hábitos espirituais:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: BookOpen, text: 'Trazer a Bíblia' },
                  { icon: Quote, text: 'Memorizar versículos' },
                  { icon: Heart, text: 'Devocionais diárias' },
                  { icon: Clock, text: 'Chegar pontualmente' },
                  { icon: Sparkles, text: 'Trazer visitantes' },
                  { icon: Star, text: 'Participar dos encontros' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
                    <item.icon className="w-5 h-5 text-[#8BC34A] flex-shrink-0" />
                    <span className="font-inter text-sm text-white font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="font-inter text-white/60 text-sm mt-6 italic">
                O crescimento espiritual se torna leve, motivador e participativo.
              </p>
            </div>
            <div className="relative flex items-center justify-center">
              <img src="/logos/logo_semente.png" alt="Logo Projeto Sementes" className="w-72 md:w-96 object-contain drop-shadow-2xl" />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-[#8BC34A]/30 rounded-full blur-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== VISÃO ===== */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <SectionTitle icon={Sparkles} label="NOSSA VISÃO" title="Plantando sementes eternas" />
          <p className="font-inter text-gray-600 text-lg leading-relaxed mb-10">
            Acreditamos que investir na vida das crianças é plantar sementes eternas. Nosso sonho é formar:
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              'Adolescentes saudáveis',
              'Jovens firmes na fé',
              'Adultos maduros espiritualmente',
            ].map((text, i) => (
              <div key={i} className="bg-[#E8F5ED] text-[#1A4D2E] rounded-full px-6 py-3 font-inter font-bold text-sm flex items-center gap-2">
                <Sprout className="w-5 h-5" /> {text}
              </div>
            ))}
          </div>
          <p className="font-inter text-gray-500 max-w-xl mx-auto">
            Uma geração capaz de impactar sua família, escola e sociedade com os valores do Reino de Deus.
          </p>
        </div>
      </section>

      {/* ===== CTA / CONTATO ===== */}
      <section id="contato" className="py-20 px-4 bg-gradient-to-b from-[#F2F7F4] to-[#FAFCFB]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-nunito text-3xl sm:text-4xl font-extrabold text-[#1A4D2E] mb-4">{c.cta_titulo}</h2>
          <p className="font-inter text-gray-600 text-lg mb-8">{c.cta_texto}</p>
          <div className="bg-[#E8F5ED] border border-[#1A4D2E]/10 rounded-2xl p-6 inline-block mb-8">
            <Sprout className="w-10 h-10 text-[#1A4D2E] mx-auto mb-2" />
            <p className="font-nunito font-bold text-[#1A4D2E]">{c.cta_frase}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-md mx-auto mb-10">
            <div className="space-y-4 text-left">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" />
                <div>
                  <p className="font-inter font-semibold text-gray-800 text-sm">{c.encontros_local_nome}</p>
                  <p className="font-inter text-xs text-gray-500">{c.cta_endereco}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#1A4D2E] flex-shrink-0" />
                <p className="font-inter font-semibold text-gray-800 text-sm">{c.cta_horario}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate('/app-pais/login')} className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8 py-6 text-base font-bold shadow-xl active:scale-95 transition-all">
              Quero Levar Meu Filho <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button onClick={() => navigate('/instalar')} variant="outline" className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F5ED] rounded-full px-8 py-6 text-base font-semibold">
              Instalar App
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#0a2914] py-10 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_RAIZES_WHITE_URL} alt="Ministério Raízes" className="h-10 object-contain" />
            <div>
              <p className="font-nunito font-bold text-white text-sm">Ministério Raízes</p>
              <p className="font-inter text-white/50 text-xs">{c.hero_local}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-end">
            <a href="https://instagram.com/raizes.if" target="_blank" rel="noopener noreferrer" className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Instagram className="w-4 h-4" /> @raizes.if
            </a>
            <a href="https://instagram.com/clubinhoif" target="_blank" rel="noopener noreferrer" className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Instagram className="w-4 h-4" /> @clubinhoif
            </a>
            <a href="https://instagram.com/igrejafamiliasorocaba" target="_blank" rel="noopener noreferrer" className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Instagram className="w-4 h-4" /> @igrejafamilia
            </a>
            <button onClick={() => navigate('/instalar')} className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Download className="w-4 h-4" /> Instalar App
            </button>
            <button onClick={() => navigate('/login')} className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Shield className="w-4 h-4" /> Area Admin
            </button>
            <button onClick={() => navigate('/app-pais/login')} className="font-inter text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              <Users className="w-4 h-4" /> Portal da Familia
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-white/10 text-center">
          <p className="font-inter text-xs text-white/30">Ministério Raízes - {c.hero_subtitulo} - {c.hero_versiculo_ref}</p>
          <p className="font-inter text-[10px] text-white/20 mt-2">&copy; 2026 KingdomCloud.com.br. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
